import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { Curriculum, Goal } from '@ilteducation/curriculum-types';
import { curriculumApi } from '@/api/curriculumApi';
import config from '@/config/ConfigProvider';

export const useCurriculumStore = defineStore('curriculum', () => {
  const curriculumData = ref<(Curriculum & { grades: string[]; goals: Goal[] })[]>([]);

  const currentCurriculumId = null;

  const sortGrades = (a: any, b: any) => {
    if (a.search('-') === 1) {
      // E.g. "1-3", "4-6"
      return Number(a.substring(0, a.indexOf('-'))) - Number(b.substring(0, b.indexOf('-')));
    }
    // E.g. "7", "10"
    return Number(a) - Number(b);
  };

  const uniqBy = (goal: Goal[], key: any, defaultLanguage: string) => {
    const seen = new Set();
    return goal.filter((item) => {
      const k = key(item.content[defaultLanguage]);
      return seen.has(k) ? false : seen.add(k);
    });
  };

  const loadCurriculumData = async (country: string, resourceId: string) => {
    if (currentCurriculumId === resourceId) {
      // Data is already loaded for this resource, no need to load it again
      return;
    }
    const curriculumGoalsResponse = await curriculumApi.getGoalsByResourceId(resourceId);

    if (curriculumGoalsResponse.data.length === 0) {
      // No curriculum is associated with this resource, reset curriculum data
      curriculumData.value = [];
      return;
    }

    const curriculumResponse = await curriculumApi.getCurriculumByCountry(country);

    curriculumData.value = curriculumResponse.data.map((curriculum: Curriculum) => {
      const grades = [...new Set(curriculumGoalsResponse.data.map((goal: Goal) => goal.grade))].sort(
        sortGrades,
      ) as string[];

      const uniqueGoals = uniqBy(curriculumGoalsResponse.data, JSON.stringify, curriculum.defaultLanguage);

      return { ...curriculum, grades, goals: uniqueGoals };
    });
  };

  const showCurriculum = computed(() => config.value('showCurriculum') && curriculumData.value.length > 0);

  return {
    curriculum: curriculumData,
    showCurriculum,
    loadCurriculumData,
  };
});
